<script setup lang="ts" generic="TabKey extends string">
    export type SelectableTab<Key extends string = string> = {
        key: Key;
        label: string;
    };

    interface Props {
        readonly tabs: readonly SelectableTab<TabKey>[];
        readonly ariaLabel?: string;
        readonly disabled?: boolean;
    }

    const { tabs, ariaLabel = '', disabled = false } = defineProps<Props>();

    const activeKey = defineModel<TabKey>({ required: true });

    function selectTab(key: TabKey) {
        if (disabled || key === activeKey.value) {
            return;
        }

        activeKey.value = key;
    }
</script>

<template>
    <div
        class="selectable-tab-buttons"
        role="tablist"
        :aria-label="ariaLabel || undefined"
        :aria-disabled="disabled || undefined"
    >
        <button
            v-for="tab in tabs"
            :key="tab.key"
            type="button"
            role="tab"
            :class="{ active: tab.key === activeKey }"
            :aria-selected="tab.key === activeKey"
            :disabled="disabled"
            :title="tab.label"
            @click="selectTab(tab.key)"
        >
            {{ tab.label }}
        </button>
    </div>
</template>

<style lang="scss" scoped>
    .selectable-tab-buttons {
        --tab-border-color: var(--black);
        --tab-active-bg: var(--black);
        --tab-active-color: var(--white);
        --tab-inactive-bg: var(--white);
        --tab-inactive-color: var(--black);
        --tab-radius: 0.5rem;
        --tab-font-size: var(--font-xs);
        --tab-padding: 0.625rem var(--spacing);

        display: flex;
        flex-direction: column;
        gap: var(--spacing--05);
        width: 100%;
        max-width: 100%;

        @media (min-width: $br-m) {
            display: inline-grid;
            grid-auto-flow: column;
            grid-auto-columns: 1fr;
            width: auto;
            overflow: hidden;
            gap: 0;
            border: 1px solid var(--tab-border-color);
            border-radius: var(--tab-radius);
            background-color: var(--tab-inactive-bg);
        }
    }

    button {
        margin: 0;
        border: 1px solid var(--tab-border-color);
        border-radius: var(--tab-radius);
        padding: var(--tab-padding);
        background-color: var(--tab-inactive-bg);
        color: var(--tab-inactive-color);
        font-size: var(--tab-font-size);
        font-weight: 600;
        line-height: normal;
        white-space: nowrap;
        text-align: center;
        cursor: pointer;
        text-overflow: ellipsis;
        overflow: hidden;

        transition:
            background-color 0.15s ease,
            color 0.15s ease;

        @media (min-width: $br-m) {
            border: 0;
            border-radius: 0;

            &:not(:last-child) {
                border-right: 1px solid var(--tab-border-color);
            }

            &:first-child {
                border-radius: calc(var(--tab-radius) - 1px) 0 0 calc(var(--tab-radius) - 1px);
            }

            &:last-child {
                border-radius: 0 calc(var(--tab-radius) - 1px) calc(var(--tab-radius) - 1px) 0;
            }
        }

        &:hover:not(:disabled):not(.active) {
            background-color: var(--off-white);
        }

        &:disabled {
            cursor: not-allowed;
            opacity: 0.75;
        }

        &:focus-visible:not(:disabled) {
            outline: 2px solid var(--color-focus);
            outline-offset: 2px;
            box-shadow: none;
            position: relative;
            z-index: 1;

            @media (min-width: $br-m) {
                outline: none;
                box-shadow: inset 0 0 0 2px var(--color-focus);
            }
        }

        &.active {
            background-color: var(--tab-active-bg);
            color: var(--tab-active-color);
        }
    }
</style>
